<template>
  <div>
    <div v-if="outplantSite">
        <div v-if="editMode">
            <SiteEdit :branchId="branchId" :siteId="outplantSiteId" :type="'outplant'" @update="editMode = false" />
        </div>
        <div v-else>
            <div class="flex items-center mb-4 ml-1">
                <BackButton class="mr-1" />
                <h1 class="text-2xl font-black font-lato mr-2">{{ outplantSite.name }}</h1>
                <div class="relative">
                    <button @click="toggleMenu" class="text-white hover:text-gray-300 transition-colors duration-300">
                        <i class="mdi mdi-dots-vertical text-xl"></i>
                    </button>
                    <div v-if="showMenu" class="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-black border border-gray-600 z-10">
                        <div class="py-1 text-white" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                            <a href="#" class="block px-4 py-2 text-sm text-white hover:bg-gray-800" role="menuitem" @click.prevent="editSite">
                                <i class="mdi mdi-pencil mr-2"></i>Edit
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <OutplantOverview 
                :site="outplantSite" 
                :outplantCells="outplantCells" 
                :overview="overview" 
                :devices="devices"
                :branchId="branchId"
                :outplantSiteId="outplantSiteId"
            />
            
            <!-- <h2 class="text-2xl font-black font-lato mb-4 mt-4 ml-1">Monitoring Schedule</h2>
            <ScheduleCard class="mb-4" :branchId="branchId" :siteId="outplantSiteId" :type="'outplant'" /> -->
            
            <h2 class="text-2xl font-black font-lato mb-4 mt-4 capitalize">Surveys</h2>
            
            <!-- Grouped Surveys by Site -->
            <div v-if="groupedSurveys && Object.keys(groupedSurveys).length > 0">
                <div v-for="(siteSurveys, siteId) in groupedSurveys" :key="siteId" class="mb-8">
                    <!-- Site Header -->
                    <div class="flex items-center mb-4">
                        <div class="bg-neutral-800 bg-opacity-90 px-3 py-1 rounded-full">
                            <span class="text-sm font-black font-lato text-white">
                                {{ getSiteName(siteId) }}
                            </span>
                        </div>
                        <div class="flex-1 h-px bg-neutral-600 ml-3"></div>
                        <span class="text-sm text-neutral-400 ml-3">
                            {{ siteSurveys.length }} survey{{ siteSurveys.length !== 1 ? 's' : '' }}
                        </span>
                    </div>
                    
                    <!-- Surveys Grid for this Site -->
                    <div class="structures-grid">
                        <SurveyCard 
                            v-for="survey in siteSurveys" 
                            :key="survey.id" 
                            :survey="survey" 
                            :branchId="branchId" 
                            :outplantSiteId="outplantSiteId"
                            :stats="surveyStats[survey.id]"
                        />
                    </div>
                </div>
                
                <!-- Add New Survey Card -->
                <!-- <div class="structures-grid">
                    <AddNewCard @card-click="addNewSurvey" />
                </div> -->
            </div>
            
            <!-- Fallback: Show surveys without grouping if no surveyPlotID -->
            <div v-else class="structures-grid">
                <SurveyCard 
                    v-for="survey in surveys" 
                    :key="survey.id" 
                    :survey="survey" 
                    :branchId="branchId" 
                    :outplantSiteId="outplantSiteId"
                    :stats="surveyStats[survey.id]"
                />
                <!-- <AddNewCard @card-click="addNewSurvey" /> -->
            </div>

            
            <!-- <h2 class="text-2xl font-black font-lato mb-4 mt-4 capitalize">Devices</h2>
            <div v-if="devices.length > 0" class="structures-grid">
                <DeviceCard v-for="device in devices" :key="device.id" :device="device" />
            </div>
            <h2 v-if="outplantCells.length > 0" class="text-2xl font-black font-lato mb-4 mt-4 capitalize">{{ outplantCells[0].type}}s
            </h2>
            <div class="structures-grid">
                <OutplantCellCard v-for="cell in sortedCells" :key="cell.id" :branchId="branchId" :outplantSiteId="outplantSiteId" :cell="cell" />
            </div> -->
        </div>
    </div>
  </div>
</template>

<script>
  import SiteEdit from './SiteEdit.vue';
  import { db_resto_new, data_url_new, storage_resto, db_map } from "../main";
  import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
  import { ref as storageRef, getDownloadURL } from 'firebase/storage';
  import OutplantOverview from './OutplantOverview.vue';
  import OutplantCellCard from './OutplantCellCard.vue';
  import DeviceCard from './DeviceCard.vue';
  import SurveyCard from './SurveyCard.vue';
  import AddNewCard from './utils/AddNewCard.vue';
  import ScheduleCard from './ScheduleCard.vue';
  import BackButton from './utils/BackButton.vue';
  import PhotosModule from "./before-after/PhotosModule.vue";

  export default {
    name: "OutplantSite",
    components: {
      OutplantOverview,
      OutplantCellCard,
      DeviceCard,
      SurveyCard,
      AddNewCard,
      SiteEdit,
      ScheduleCard,
      BackButton,
      PhotosModule,
    },
    props: {
      branchId: String,
      outplantSiteId: String,
    },
    data() {
       return {
            overview: null,
            outplantSite: null,
            outplantCells: [],
            fragments: [],
            devices: [],
            surveys: [],
            groupedSurveys: {},
            sitesData: {}, // Store site information by siteId
            surveyStats: {}, // Store stats data for each survey
            showMenu: false,
            editMode: false,
        };
    },
    watch: {
      $route: {
        immediate: true,
        handler() {
          this.fetchOutplantData();
          this.fetchOverviewData();
        },
      },
    },
    computed: {
      sortedCells() {
        return this.outplantCells.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { numeric: true })
        );
      },
    },
    methods: {
      async fetchOverviewData() {
        try {
          const response = await fetch(
            `${data_url_new}/data/branch?branch=${this.branchId}&pwd=showmethedata`
          );
          if (!response.ok) {
            throw new Error("Network response was not ok");
          }
          const global = await response.json();
          this.overview = global.branch.outplant_info.find(
            (outplant) => outplant.outplantID === this.outplantSiteId
          );
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      },
      async fetchOutplantData() {
        try {
          // Clear existing data
          this.outplantSite = null;
          this.outplantCells = [];
          this.surveys = [];

          // Fetch outplant site data
          const outplantSiteResponse = await fetch(`${data_url_new}/data/raw/object?collection=_sites&id=${this.outplantSiteId}&pwd=showmethedata`);
          const outplantSiteResponseData = await outplantSiteResponse.json();

          if (outplantSiteResponseData.object) {
            this.outplantSite = {
              id: this.outplantSiteId,
              ...outplantSiteResponseData.object,
            };
            if (this.outplantSite.siteData.reefos_id)
              this.getDeviceInfo(this.outplantSite.siteData.reefos_id);

            // Fetch outplant cells
            // const outplantCellsQuery = query(collection(db_resto_new, '_sites'),
            //         where('location.outplantID', '==', this.outplantSiteId),
            //         where('siteType', '==', 'outplantCell'));

            // const querySnapshot = await getDocs(outplantCellsQuery);
            // querySnapshot.forEach((doc) => {
            //   this.outplantCells.push({ id: doc.id, ...doc.data() });
            // });

            // Fetch surveys
            await this.fetchSurveys();

            // await this.fetchFragments(); <-- takes forever to load
          } else {
            console.log("No such outplant site!");
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      },
      // TODO(reefos-data-api): no listing/single-doc endpoint exists for db_map
      // (the separate cg-reefmap project)'s Cameras collection -- same class of
      // gap as fetchDevices() in Header.vue/Global.vue/Devices.vue. Left as a
      // direct read pending a future backend addition.
      async getDeviceInfo(reefosId) {
        try {
            const deviceRef = doc(db_map, `Cameras/${reefosId}`);
            const deviceDoc = await getDoc(deviceRef);

            if (deviceDoc.exists()) {
                this.devices.push({ id: deviceDoc.id, ...deviceDoc.data() });
            } else {
                console.log('No such device!');
            }
        } catch (error) {
            console.error("Error fetching device data:", error);
        }
      },

      async fetchFragments() {
        try {
          // Get all cell IDs for the current outplant site
          const cellIds = this.outplantCells.map((cell) => cell.id);

          const fragmentsRef = query(collection(db_resto_new, '_statistics'),
                                where('location.branchId', '==', this.branchId),
                                where('stattype', '==', 'fragment_stats'));
          const querySnapshot = await getDocs(fragmentsRef);

          // Filter fragments based on the cell IDs
          this.fragments = querySnapshot.docs
            .filter((doc) => {
              const data = doc.data();
              return (
                data.outplantInfo &&
                cellIds.includes(data.outplantInfo.outplantCellID)
              );
            })
            .map((doc) => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
          console.error("Error fetching fragments:", error);
        }
      },
      async fetchSurveys() {
          try {
              const response = await fetch(`${data_url_new}/data/raw/events?outplant=${this.outplantSiteId}&type=coralSurvey&pwd=showmethedata`);
              const responseData = await response.json();
              this.surveys = (responseData.events || []).map(event => ({
                  id: event.doc_id,
                  ...event
              }));

              // Sort surveys by date (most recent first)
              // NOTE: pre-existing bug, not introduced here -- real survey docs have
              // no top-level `date` field (only `completedAt` and the nested
              // `eventData.surveyData.date`), so `a.date`/`b.date` are always
              // undefined and this sort is a no-op. Left as-is, not in scope.
              this.surveys.sort((a, b) => {
                  const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
                  const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
                  return dateB - dateA;
              });
              
              // Group surveys by surveyPlotID and fetch site information
              await this.groupSurveysByPlot();
              
          } catch (error) {
              console.error('Error fetching surveys:', error);
          }
      },
      async groupSurveysByPlot() {
          try {
              // Reset grouped surveys
              this.groupedSurveys = {};
              
              // Group surveys by surveyPlotID (handle both surveyPlotId and surveyPlotID)
              const plotGroups = {};
              this.surveys.forEach(survey => {
                  // Check for both variations: surveyPlotId and surveyPlotID
                  const plotId = survey.location?.surveyPlotID || survey.location?.surveyPlotId;
                  if (plotId) {
                      if (!plotGroups[plotId]) {
                          plotGroups[plotId] = [];
                      }
                      plotGroups[plotId].push(survey);
                  }
              });
              
              // Fetch site information and stats for each plot
              const sitePromises = Object.keys(plotGroups).map(async (plotId) => {
                  try {
                      const siteData = await this.fetchSiteData(plotId);
                      if (siteData) {
                          this.$set(this.sitesData, plotId, siteData);
                          this.$set(this.groupedSurveys, plotId, plotGroups[plotId]);
                          
                          // Fetch stats for each survey in this plot
                          const surveyPromises = plotGroups[plotId].map(async (survey) => {
                              const stats = await this.fetchSurveyStats(survey);
                              if (stats) {
                                  this.$set(this.surveyStats, survey.id, stats);
                              }
                          });
                          
                          await Promise.all(surveyPromises);
                      }
                  } catch (error) {
                      console.error(`Error fetching site data for plot ${plotId}:`, error);
                  }
              });
              
              await Promise.all(sitePromises);
              
              // Force reactivity update
              this.$forceUpdate();
              
          } catch (error) {
              console.error('Error grouping surveys by plot:', error);
          }
      },
      async fetchSiteData(siteId) {
          try {
              const response = await fetch(`${data_url_new}/data/raw/object?collection=_sites&id=${siteId}&pwd=showmethedata`);
              const responseData = await response.json();

              if (responseData.object) {
                  return { id: siteId, ...responseData.object };
              } else {
                  console.warn(`Site ${siteId} not found`);
                  return null;
              }
          } catch (error) {
              console.error(`Error fetching site data for ${siteId}:`, error);
              return null;
          }
      },
      getSiteName(siteId) {
          const siteData = this.sitesData[siteId];
          return siteData?.name || `Site ${siteId}`;
      },
      async fetchSurveyStats(survey) {
          try {
              // Get the dataPath from the survey
              const dataPath = survey.eventData?.surveyData?.gcsPath;
              if (!dataPath) {
                  console.warn(`No dataPath found for survey ${survey.id}`);
                  return null;
              }

              // Get the download URL for parsed.txt from Firebase Storage
              const storagePath = `${dataPath}/output/parsed.txt`;
              const fileRef = storageRef(storage_resto, storagePath);
              const downloadUrl = await getDownloadURL(fileRef);
              
              const response = await fetch(downloadUrl);
              if (!response.ok) {
                  throw new Error(`HTTP error! status: ${response.status}`);
              }
              
              const text = await response.text();
              return this.parseStatisticsData(text);
              
          } catch (error) {
              console.error(`Error fetching stats for survey ${survey.id}:`, error);
              return null;
          }
      },
      parseStatisticsData(text) {
          const lines = text.trim().split('\n');
          const data = {};
          
          lines.forEach(line => {
              if (line.trim()) {
                  const [key, value] = line.split(',');
                  data[key] = value;
              }
          });
          
          return data;
      },
      toggleMenu() {
        this.showMenu = !this.showMenu;
      },
      addNewSurvey() {
            this.$router.push({
                name: 'AddSurvey',
                params: {
                    branchId: this.branchId,
                    outplantSiteId: this.outplantSiteId
                }
            });
      },
      editSite() {
        this.editMode = true;
        this.showMenu = false;
      },
      updateSite(updatedSite) {
        this.outplantSite = { ...this.outplantSite, ...updatedSite };
        this.editMode = false;
        // You might want to refresh data or update Firestore here
      },
      cancelEdit() {
        this.editMode = false;
      },
      addNewSurvey() {
        // TODO: Implement add new survey functionality
        console.log('Add new survey clicked');
      },
    },
  };

</script>

<style scoped>
  .structures-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1rem;
    align-items: start;
  }
</style>
